/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "../Localization";

export type ErrorRendererProps = {
  error?: Error | string | null;
  message?: string;
  onRetry?: () => void;
  /** Disables the retry button and spins its icon while a retry is in flight. */
  retrying?: boolean;
  /** Renders a connectivity line under the message. */
  offline?: "offline" | "degraded" | false;
  severity?: "error" | "warning";
  /** Overrides the button's default "Retry"/"Retrying…" labels for a more specific action. */
  retryLabel?: string;
  retryingLabel?: string;
};

export const ErrorRenderer = defineComponent<ErrorRendererProps>({
  name: "ErrorRenderer",
  props: [
    "error",
    "message",
    "onRetry",
    "retrying",
    "offline",
    "severity",
    "retryLabel",
    "retryingLabel",
  ] as any,
  setup(props) {
    const { t } = useLocalization();

    return () => {
      const msg =
        props.message ??
        (props.error instanceof Error
          ? props.error.message
          : typeof props.error === "string"
            ? props.error
            : t("general.errors.something_went_wrong"));

      const isWarning = props.severity === "warning";
      const tone = isWarning ? "text-warning" : "text-error";
      const badgeTone = isWarning ? "bg-warning/10" : "bg-error/10";
      const canRetry = Boolean(props.onRetry);
      // Same icon the global NetworkStatusProvider banner uses for a
      // connectivity problem, so the two read as the same kind of failure
      // rather than a generic app error.
      const icon = props.offline ? "wifi_off" : "error_outline";

      return (
        <div class="flex flex-col items-center justify-center gap-6 py-12">
          <div class="flex flex-col items-center justify-center gap-3">
            <div
              class={`flex h-14 w-14 items-center justify-center rounded-full ${badgeTone}`}
            >
              <span class={`material-symbols-outlined text-3xl ${tone}`}>
                {icon}
              </span>
            </div>
            <p class="max-w-sm text-center text-base font-medium text-base-content">
              {msg}
            </p>
            {props.offline === "offline" ? (
              <p class={`max-w-sm text-center text-xs font-medium ${tone}`}>
                {t("general.errors.device_offline")}
              </p>
            ) : props.offline === "degraded" ? (
              <p class="max-w-sm text-center text-xs text-base-content/60">
                {t("general.errors.connection_unstable")}
              </p>
            ) : null}
          </div>
          {props.onRetry && (
            <button
              class="btn btn-secondary"
              disabled={props.retrying || !canRetry}
              onClick={props.onRetry}
            >
              <span
                class={`material-symbols-outlined text-2xl ${props.retrying ? "animate-spin" : ""}`}
              >
                refresh
              </span>
              {props.retrying
                ? props.retryingLabel || t("general.errors.retrying")
                : props.retryLabel || t("general.words.retry")}
            </button>
          )}
        </div>
      );
    };
  },
});
